import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtError, fmtDateTime } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Warning, CheckCircle, ArrowsClockwise, ArrowUpRight, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";

const SEVERITY_STYLE = {
    high: { color: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5", label: "High" },
    medium: { color: "#B45309", bg: "#FEF3C7", border: "#FCD34D", label: "Medium" },
    low: { color: "#475569", bg: "#F1F5F9", border: "#CBD5E1", label: "Low" },
};

const TYPE_LABELS = {
    pace_underpace: "Pace below threshold",
    wow_collapse: "Week-over-week collapse",
    missing_eod: "Missing EOD report",
};

export default function Anomalies() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("open");

    const canScan = ["super_admin", "execution_head", "delivery_head", "client_servicing"].includes(user?.role);
    const canResolve = ["super_admin", "execution_head", "delivery_head"].includes(user?.role);

    const load = async () => {
        try {
            const { data } = await api.get(`/anomalies?status=${filter}`);
            setItems(data);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filter]);

    const scan = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/anomalies/scan");
            toast.success(
                `Scan complete: ${data.new_count} new · ${data.refreshed_count} refreshed · ${data.open_count} open`,
            );
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const acknowledge = async (id) => {
        try {
            await api.post(`/anomalies/${id}/acknowledge`);
            toast.success("Acknowledged");
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };

    const resolve = async (id) => {
        try {
            await api.post(`/anomalies/${id}/resolve`);
            toast.success("Resolved");
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };

    const grouped = items.reduce(
        (acc, a) => {
            (acc[a.severity] = acc[a.severity] || []).push(a);
            return acc;
        },
        { high: [], medium: [], low: [] },
    );

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// First line of defense</div>
                    <h1 className="font-display text-4xl tracking-tighter">Anomaly alerts</h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        Real-time signals when delivery pace drops, WoW collapses, or EOD reports go missing.
                        Severity-routed to the right team.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex border border-slate-200 bg-white" data-testid="anomalies-filter">
                        {["open", "resolved", "all"].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                data-testid={`anomalies-filter-${f}`}
                                className={`px-3 py-2 text-xs font-mono uppercase tracking-wider ${
                                    filter === f ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                    {canScan && (
                        <Button
                            onClick={scan}
                            disabled={busy}
                            data-testid="anomalies-scan-button"
                            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2"
                        >
                            <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
                            {busy ? "Scanning…" : "Run scan now"}
                        </Button>
                    )}
                </div>
            </header>

            {/* Severity strip */}
            <section className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                {["high", "medium", "low"].map((sev) => {
                    const meta = SEVERITY_STYLE[sev];
                    return (
                        <div key={sev} className="bg-white p-5" data-testid={`anomalies-severity-${sev}`}>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5" style={{ backgroundColor: meta.color }} />
                                <span className="overline">{meta.label}</span>
                            </div>
                            <div className="font-mono text-3xl mt-2 tracking-tighter">{grouped[sev].length}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                {sev === "high" ? "needs immediate action" : sev === "medium" ? "review today" : "monitor"}
                            </div>
                        </div>
                    );
                })}
            </section>

            {loading && <div className="text-xs font-mono text-slate-400">loading…</div>}

            {!loading && items.length === 0 && (
                <div className="bg-white border border-slate-200 p-12 text-center" data-testid="anomalies-empty">
                    <CheckCircle weight="fill" size={32} className="text-green-700 mx-auto" />
                    <h2 className="font-display text-2xl mt-3">All clear</h2>
                    <p className="text-sm text-slate-500 mt-1">No anomalies match the current filter. Run a scan to refresh.</p>
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="space-y-3" data-testid="anomalies-list">
                    {items.map((a) => (
                        <AnomalyRow
                            key={a.id}
                            a={a}
                            canResolve={canResolve}
                            onAck={() => acknowledge(a.id)}
                            onResolve={() => resolve(a.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function AnomalyRow({ a, canResolve, onAck, onResolve }) {
    const meta = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.medium;
    const isOpen = !a.resolved_at;
    const recs = a.recommendations || [];
    return (
        <div
            className="bg-white border border-slate-200 p-5 flex flex-col gap-4"
            data-testid={`anomaly-row-${a.id}`}
            style={{ borderLeft: `3px solid ${meta.color}` }}
        >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                            className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5"
                            style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.border}` }}
                        >
                            {meta.label}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5">
                            {TYPE_LABELS[a.type] || a.type}
                        </span>
                        {a.acknowledged_by && isOpen && (
                            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">acknowledged</span>
                        )}
                        {!isOpen && (
                            <span className="font-mono text-[10px] uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-2 py-0.5">
                                Resolved
                            </span>
                        )}
                    </div>
                    <div className="font-display text-lg">{a.title}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{a.body}</div>
                    <div className="overline text-slate-400 mt-2">
                        first seen {fmtDateTime(a.created_at)} · last seen {fmtDateTime(a.last_seen_at)}
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <Link
                        to={`/campaigns/${a.campaign_id}`}
                        data-testid={`anomaly-campaign-link-${a.id}`}
                        className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline inline-flex items-center gap-1"
                    >
                        Open campaign <ArrowUpRight size={12} />
                    </Link>
                    {isOpen && (
                        <>
                            {!a.acknowledged_by && (
                                <Button
                                    onClick={onAck}
                                    variant="outline"
                                    data-testid={`anomaly-ack-${a.id}`}
                                    className="rounded-none border-slate-300 h-8 text-xs"
                                >
                                    Acknowledge
                                </Button>
                            )}
                            {canResolve && (
                                <Button
                                    onClick={onResolve}
                                    data-testid={`anomaly-resolve-${a.id}`}
                                    className="rounded-none bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs gap-1.5"
                                >
                                    <CheckCircle size={12} /> Resolve
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Suggested actions */}
            {isOpen && recs.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 p-3" data-testid={`anomaly-recs-${a.id}`}>
                    <div className="overline mb-2" style={{ fontSize: 9 }}>Suggested actions · auto-generated</div>
                    <ul className="space-y-2">
                        {recs.map((r, i) => (
                            <li
                                key={i}
                                className={`flex items-start gap-3 ${r.severity === "primary" ? "" : "opacity-80"}`}
                                data-testid={`anomaly-rec-${a.id}-${i}`}
                            >
                                <span
                                    className="w-1.5 h-1.5 mt-2 shrink-0"
                                    style={{ backgroundColor: r.severity === "primary" ? meta.color : "#a1a1aa" }}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">
                                        <RecommendationTrigger rec={r} campaignId={a.campaign_id} />
                                    </div>
                                    <div className="text-xs text-slate-600 mt-0.5">{r.hint}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function RecommendationTrigger({ rec, campaignId }) {
    const targetTab = {
        open_delivery: "delivery",
        open_reports: "reports",
        open_creatives: "creatives",
    }[rec.action];
    if (targetTab) {
        return (
            <Link
                to={`/campaigns/${campaignId}?tab=${targetTab}`}
                className="text-slate-900 hover:underline"
                data-testid={`anomaly-rec-link-${rec.action}`}
            >
                {rec.label} <ArrowUpRight size={11} className="inline align-baseline" />
            </Link>
        );
    }
    return <span className="text-slate-900">{rec.label}</span>;
}

